import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createDispatchHook } from 'react-redux';
import { resetAuth } from '../store/authSlice';
import { RootState } from '../store/store';
import { AttendeeI, CompanyI, EventsI, FaqI, SpeakerI } from './types';

const transformResponse = (response: any) => {
  if (response.data.result) {
    return {
      ...response.data,
      result: response.data.result.map((e: any) => ({ ...e, id: e._id })),
    };
  }
  return response.data;
};
const transformErrorResponse = (
  baseQueryReturnValue: any,
  meta: any,
  arg: any
) => {
  // console.log('debug', baseQueryReturnValue, meta, arg);
  return baseQueryReturnValue.data;
};

export const rtkQueryApi = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/admin/v1',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
    validateStatus: (response, body) => body.success,
  }),
  tagTypes: ['Generic'],
  endpoints: (builder) => ({
    //Auth
    login: builder.mutation({
      query: (body: { username: string; password: string }) => ({
        url: '/auth/login',
        method: 'post',
        body,
      }),
      transformResponse,
      transformErrorResponse,
      invalidatesTags: ['Generic'],
    }),
    renewToken: builder.mutation({
      query: (body: {}) => ({
        url: '/auth/renew-token',
        method: 'post',
        body: {},
      }),
      transformResponse,
      transformErrorResponse,
      invalidatesTags: ['Generic'],
    }),
    logout: builder.mutation({
      query: () => ({
        url: '/auth/logout',
        method: 'post',
        body: {},
      }),
      transformResponse,
      transformErrorResponse,
      invalidatesTags: ['Generic'],
    }),
    //Generic
    getAll: builder.query({
      query: ({ resource, page, search, pageSize }) => ({
        url: `/app/${resource}?page=${page}&search=${search}&pageSize=${pageSize}`,
        method: 'get',
      }),
      providesTags: ['Generic'],
      transformResponse,
      transformErrorResponse,
    }),
    getDashboard: builder.query({
      query: ({}) => ({
        url: '/app/stats/dashboard',
        method: 'get',
      }),
      providesTags: ['Generic'],
      transformResponse,
      transformErrorResponse,
    }),
    getChangeType: builder.query({
      query: ({}) => ({
        url: '/app/stats/change-types',
        method: 'get',
      }),
      providesTags: ['Generic'],
      transformResponse,
      transformErrorResponse,
    }),
    getSingle: builder.query<any, any>({
      query: ({ resource, id }) => ({
        url: `/app/${resource}/${id}`,
        method: 'get',
      }),
      providesTags: ['Generic'],
      transformResponse,
      transformErrorResponse,
    }),
    delete: builder.mutation<any, any>({
      query: ({ resource, id }) => ({
        url: `/app/${resource}/${id}`,
        method: 'delete',
      }),
      invalidatesTags: ['Generic'],
      transformResponse,
      transformErrorResponse,
    }),
    upsert: builder.mutation<any, any>({
      query: ({ resource, body, id }) => ({
        url: `/app/${resource}/${id}`,
        body,
        method: 'post',
      }),
      invalidatesTags: ['Generic'],
      transformResponse,
      transformErrorResponse,
    }),
    testContactUs: builder.mutation<any, any>({
      query: ({ body }) => ({
        url: '/app/contact-us/',
        body,
        method: 'post',
      }),
      invalidatesTags: ['Generic'],
      transformResponse,
      transformErrorResponse,
    }),
  }),
});

export const {
  //Auth
  useLoginMutation,
  useLogoutMutation,
  useRenewTokenMutation,
  //Generic
  useGetDashboardQuery,
  useGetChangeTypeQuery,
  useGetAllQuery,
  useGetSingleQuery,
  useDeleteMutation,
  useUpsertMutation,
  //contactUsTester
  useTestContactUsMutation,
} = rtkQueryApi;
